/*
 * @Author: YangTao
 * @Description: 匹配正则编辑窗口
 */

import { Drawer, Button, Radio, Space, Input, Row, message, Tooltip } from 'antd';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'umi';
import ExchangeIcon from '@/components/exchange-icon';
import { arrayMoveImmutable } from 'array-move';
import { CloseCircleOutlined } from '@ant-design/icons';
import _ from 'lodash';
import { Edge } from 'react-flow-renderer';

const { Search } = Input;

enum TitleType {
  反向否定查找 = "反向否定查找",
  正向否定查找 = "正向否定查找A",
  正向否定查找2 = "正向否定查找B",
  匹配x或者y = "匹配x或者y"
}

type regexType = { 
  title: TitleType; 
  regex: RegExp;
  format: (value: any) => string[];
  count: number;
  getResult: (value: string[][]) => string;
  description: string;
}

const defaultKeywords: string[] = ["不", "非", "对", "是", "乳腺结节","乳腺纤维瘤","乳房肿块","甲状腺结节","甲状腺囊肿","甲状腺炎","甲减","甲亢","乙肝","大三阳","小三阳","甲癌","甲状腺癌","甲状腺乳头癌","肺结节","肺部结节","肺部毛玻璃结节","毛玻璃结节","肺部微小结节","子宫肌瘤","宫颈炎","体检异常","血红蛋白","贫血","前列腺","痔疮","宫颈息肉","指标异常","鼻中隔","心律不齐","心率过缓","心率过速","t波","肝囊肿","肾结石","肾囊肿","肾结晶","胆固醇","甘油三酯","高血压","糖尿病","高血糖","血糖高","肠炎","肠息肉","胃炎","胃息肉","尿道炎","少儿门诊","门诊"];

/** @description 数组去重 */
const handleDeduplicate = (arr: string[]) => {
  return Array.from(new Set(arr));
}

const regexTypes: regexType[] = [
  { 
    title: TitleType.反向否定查找, 
    regex: /\(\?\<\!(.*)\)\((.*)\)/, 
    format: (value: any) => {
      return [value[1], value[2]];
    },
    count: 2,
    getResult: (value: string[][]) => {
      if (!value[0] || !value[1]) return '';
      return `(?<!${handleDeduplicate(value[0]).join('|')})(${handleDeduplicate(value[1]).join('|')})`
    },
    description: '【(?<!y)x】当x前面不是y时则匹配',
  },
  { 
    title: TitleType.正向否定查找, 
    regex: /\^\(\?\!\.\*\((.*)\)\)/, 
    format: (value: any) => [value[1]],
    count: 1,
    getResult: (value: string[][]) => {
      if (!value[0]) return '';
      return `^(?!.*(${handleDeduplicate(value[0]).join('|')}))`
    },
    description: '【^(?!.*x)】由第一个字符开始，不是x时则匹配',
  },
  { 
    title: TitleType.正向否定查找2, 
    regex: /\^\(\?\!\(\.\*(.*)\)\)/, 
    format: (value: any) => [value[1]],
    count: 1,
    getResult: (value: string[][]) => {
      if (!value[0]) return '';
      return `^(?!(.*${handleDeduplicate(value[0]).join('|')}))`
    },
    description: '【^(?!(.*x))】由第一个字符开始，不是x时则匹配（与"正向否定查找A"效果一致）',
  },
  { 
    title: TitleType.匹配x或者y, 
    regex: /.*\|?/,
    format: (value: any) => [value[0]],
    count: 1,
    getResult: (value: string[][]) => {
      if (!value[0]) return '';
      return `${handleDeduplicate(value[0]).join('|')}`
    },
    description: '【x|y】当字符是x或y时则匹配',
  },
]

type IndexProps = {
  handleEdgeUpdate: (pre: string, next: string, nodeId: string) => void;
}

const Index: React.FC<IndexProps> = ({ handleEdgeUpdate }) => {
  const [regexType, setRegexType] = useState<TitleType>(TitleType.匹配x或者y);
  const [defaultRegexType, setDefaultRegexType] = useState<string>("");
  const [list, setList] = useState<string[][]>([[]]);
  const [currentListIndex, setCurrentListIndex] = useState<number>(0);
  const [keyword, setKeyword] = useState<string>('');
  const [keywords, setKeywords] = useState<string[]>([]);

  const regexList = useSelector((state: any) => state.open.regexList);
  const conditionEdit = useSelector((state: any) => state.open.conditionEdit);
  const edges: Edge[] = useSelector((state: any) => state.open.edges);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!conditionEdit.show) {
      setList([[]]);
      setCurrentListIndex(0);
      setRegexType(TitleType.匹配x或者y);
      setDefaultRegexType("");
      setKeyword("");
    }
  }, [conditionEdit.show]);

  useEffect(() => {
    setCurrentListIndex(0);
    let r = _.find(regexTypes, o => o.title === regexType);
    if (list.length !== r?.count) {
      let arr: string[][] = [];
      new Array(r?.count).fill(0).forEach((vo: number, index: number) => {
        arr[index] = list[index] ?? [];
      })
      setList(arr);
    }
  }, [regexType]);

  /** @description 根据该节点的其他已知规则生成托底规则 */
  const handleCreateByHistory = () => {
    const baseRegexType = regexType;
    const regex = regexTypes.find(r => r.title === baseRegexType);
    if (regexType === baseRegexType && conditionEdit.nodeId) {
      const source = conditionEdit.nodeId;
      const same_source_edges = edges.filter((e: Edge) => e.source === source);
      const the_value = same_source_edges.find((e: Edge) => regex?.regex.test((e.label as string)));
      let result: string[] = [];
      same_source_edges.forEach((e: Edge) => {
        let value: string[] = [];
        for (let i = 0; i < regexTypes.length; i++) {
          if (the_value && the_value.id === e.id) continue;
          const r = regexTypes[i];
          let label = e.label as string;
          if (r.regex.test(label)) {
            if ([TitleType.正向否定查找, TitleType.正向否定查找2].includes(r.title)) break;
            let res = r.format(label.match(r.regex));
            value = res.length > 1 ? res[res.length - 1].split('|') : res[0].split('|');
            break;
          }
        }
        result = [ ...result, ...value ];
      })
      setList([result]);
      message.success("操作成功！");
    }
  }

  useEffect(() => {
    if (conditionEdit.condition && conditionEdit.show) {
      const condition = conditionEdit.condition;
      let matchString = "";
      const list = [];
      for (let i = 0; i < regexTypes.length; i++) {
        let vo: regexType = regexTypes[i];
        let isMatch = vo.regex.test(condition);
        if (isMatch) {
          let matches = condition.match(vo.regex);
          let values = vo.format(matches);
          for (let j = 0; j < vo.count; j++) {
            let value = values[j];
            list[j] = value.split('|');
          }
          setRegexType(vo.title);
          setDefaultRegexType(vo.title);
          break;
        }
      }

      if (!matchString) matchString = condition;
      let arr: string[] = [];
      list.forEach((vo) => {
        arr = [...arr, ...vo];
      })
      setKeywords(Array.from(new Set(arr)));
      setList(list);
    } else {
      setDefaultRegexType("");
    }
  }, [conditionEdit.condition, conditionEdit.show]);

  const getConditionItemList = (data: string) => {
    let value: string[] = [];
    for (let i = 0; i < regexTypes.length; i++) {
      let r = regexTypes[i];
      if (r.regex.test(data)) {
        if ([TitleType.正向否定查找, TitleType.正向否定查找2].includes(r.title)) {
          break;
        };
        let result = r.format(data.match(r.regex));
        let arr: string[] = [];
        result.forEach((vo: string) => {
          arr = [...arr, ...vo.split("|")];
        })
        value = arr;
        break;
      }
    }
    return value;
  }

  /** @description 由已经存在的edge获得的关键词 */
  const baseKeywords: string[] = useMemo(() => {
    if (regexList.length === 0) return [];
    const value = regexList.map((vo: { value: string }) => getConditionItemList(vo.value)).reduce((pre: string[], current: string[]) => {
      return [...pre, ...current]
    });
    return handleDeduplicate(value);
  }, [regexList]);

  /** @description 可选关键词 */
  const keywordList: string[] = useMemo(() => {
    if (!keyword) return handleDeduplicate([...keywords, ...baseKeywords, ...defaultKeywords]);
    return handleDeduplicate([...keywords, ...baseKeywords, ...defaultKeywords].filter((k: string) => k.indexOf(keyword) > -1));
  }, [keyword, keywords, baseKeywords]);

  const result: string = useMemo(() => {
    let r = _.find(regexTypes, r => r.title === regexType);
    return r?.getResult(list) ?? '';
  }, [list, regexType]);

  

  const containers = useMemo(() => {
    let r = _.find(regexTypes, o => o.title === regexType);
    return (new Array(r?.count)).fill([]);
  }, [regexType])

  const handleKeyword = (value: string) => {
    if (!value) return;
    if (keywordList.includes(value)) return message.warning("关键字不能重复！");
    setKeywords([value, ...keywords]);
    message.success("添加成功！");
  }

  const onClose = () => {
    dispatch({
      type: 'open/handleConditionEdit',
      payload: { show: false, isConfirm: false, } 
    })
  }

  const onConfirm = () => {
    if (!result) return message.warning("正则不存在！");
    let payload = {
      ...conditionEdit,
      show: false,
      isConfirm: false,
      condition: result,
    } 
    if (!conditionEdit.isEdit) {
      payload['isConfirm'] = true;
    } else {
      handleEdgeUpdate(conditionEdit.condition, result, conditionEdit.nodeId);
    }
    dispatch({
      type: 'open/handleConditionEdit',
      payload,
    })
  }

  const deleteListItem = (selfIndex: number, text: string) => {
    let oldList = JSON.parse(JSON.stringify(list));
    let oldValue = oldList[selfIndex];
    const oldIndex = _.findIndex(oldValue, function (o) {
      return o === text;
    });
    _.pullAt(oldValue, oldIndex);
    setList(oldList);
  }

  const onSortEnd = (key: string, num: number, currentIndex: number) => {
    let oldList = JSON.parse(JSON.stringify(list));
    let oldValue = list[currentIndex];
    const oldIndex = _.findIndex(oldValue, function (o) {
      return o === key;
    });
    const newIndex = oldIndex + num;
    const newValue = arrayMoveImmutable(oldValue, oldIndex, newIndex);
    oldList[currentIndex] = newValue;
    setList(oldList);
  };

  return <Drawer 
    title="图形化定义正则"
    visible={conditionEdit.show} 
    width="600"
    placement="left"
    onClose={onClose}
  >
    <Space direction="vertical" style={{ width: '100%' }}>
      <h3>正则类别</h3>
      <Radio.Group value={regexType} onChange={(e) => { setRegexType(e.target.value) }}>
        {
          regexTypes.map((r) => <Radio.Button key={r.title} value={r.title} disabled={conditionEdit.isEdit && defaultRegexType !== r.title}>
            <Tooltip title={r.description}>
              { r.title }
            </Tooltip>
          </Radio.Button>)
        }
      </Radio.Group>
      <h3>新建或选择关键词</h3>
      <Space>
        <Input 
          placeholder="关键字查询关键词" 
          allowClear
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)} 
        />
        <Search
          placeholder="自定义关键词"
          allowClear
          enterButton="添加"
          onSearch={handleKeyword}
        />
      </Space>
      
      <div>
        {
          keywordList.map((value, index) => <Button 
            key={value + index} 
            type="dashed"
            shape="round"
            onClick={() => {
              if (list[currentListIndex].includes(value)) return message.warning("已存在该关键字！");
              let oldList = JSON.parse(JSON.stringify(list));
              oldList[currentListIndex].push(value);
              setList(oldList);
            }}
          >{ value }</Button>)
        }
      </div>
      
      <h3>组合关键词</h3>
      {
        containers && containers.map((value, index) => <div key={index}>
          { containers.length > 1 && <Row><h4>第{ index + 1 }部分</h4></Row> }
          <Row>
            <Container 
              onSortEnd={onSortEnd} 
              list={list[index]} 
              currentIndex={currentListIndex} 
              selfIndex={index}
              onClick={(index: number) => setCurrentListIndex(index)}
              deleteListItem={deleteListItem}
            />
          </Row>
        </div>)
      }
      {
        [TitleType.正向否定查找, TitleType.正向否定查找2].includes(regexType) && <Button type="primary" onClick={handleCreateByHistory}>根据已有信息自动生成</Button>
      }
      <h3>组合结果</h3>
      <span>{ result ? result : '组合结果不存在' }</span>
      <Button type="primary" onClick={onConfirm}>{ conditionEdit.isEdit ? '确定编辑' : '确定' }</Button>
    </Space>
  </Drawer>
}

type ContainerProps = {
  list: string[];
  selfIndex: number;
  currentIndex: number;
  onSortEnd: (text: string, index: number, currentIndex: number) => void;
  onClick: (index: number) => void;
  deleteListItem: (selfIndex: number, text: string) => void;
}

const Container: React.FC<ContainerProps> = ({ list, onSortEnd, currentIndex, onClick, selfIndex, deleteListItem }) => {
  let containerStyle = {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    marginBottom: '.5rem',
    backgroundColor: 'white',
    cursor: 'pointer',
    border: '1px dashed #d2dae2',
  } as React.CSSProperties;

  if (currentIndex === selfIndex) containerStyle['backgroundColor'] = 'rgb(242, 235, 233)';

  const boxStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px dashed #d2dae2',
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    cursor: 'default',
    position: 'relative',
  } as React.CSSProperties;

  const deleteStyle = {
    width: 20,
    height: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  }

  const renderCard = useCallback((box: string, index: number) => {
    const rotateStyle = {
      transform: 'rotate(0deg)',
      color: '#1890ff'
    } as React.CSSProperties;
    const style = {
      right: '-10px',
      top: '50%',
      transform: 'translateY(-50%)',
      borderColor: '#1890ff',
    } as React.CSSProperties;

    return <div style={boxStyle} key={box + index}>
      {box}
      <Tooltip title="删除">
        <div style={deleteStyle} onClick={() => deleteListItem(selfIndex, box)}>
          <CloseCircleOutlined />
      </div>
      </Tooltip>
      {
        index < (list.length - 1) && <ExchangeIcon 
          rotateStyle={rotateStyle}
          style={style}
          onSort={() => onSortEnd(box, 1, selfIndex)} 
        />
      }
    </div>
  },[list])

  return <div style={containerStyle} onClick={() => onClick(selfIndex)}>
    { list && list.length ? list.map((card, i) => renderCard(card, i)) : '请点击关键词'}
  </div>
}

export default React.memo(Index);
